import React, { useState } from "react";
import { Calendar, ChevronRight } from "lucide-react";

const getStatusColor = (status, theme) => {
  switch (status) {
    case "approved": return theme === "dark" ? "#10b981" : "#059669";
    case "pending":  return theme === "dark" ? "#f59e0b" : "#d97706";
    case "rejected": return theme === "dark" ? "#ef4444" : "#dc2626";
    default:         return theme === "dark" ? "#2e7cba" : "#2e7cba";
  }
};

const getStatusIcon = (status) => {
  const { CheckCircle, Clock, XCircle, AlertCircle } = require("lucide-react");
  switch (status) {
    case "approved": return CheckCircle;
    case "pending":  return Clock;
    case "rejected": return XCircle;
    default:         return AlertCircle;
  }
};

const getLeaveTypeIcon = (type) => {
  const { Sun, Heart, Home, AlertCircle, Calendar } = require("lucide-react");
  switch (type) {
    case "annual":    return Sun;
    case "sick":      return Heart;
    case "maternity": return Home;
    case "emergency": return AlertCircle;
    case "wfh":       return Home;
    default:          return Calendar;
  }
};

const calculateDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end   = new Date(endDate);
  return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
};

const getStatusText = (status, t) => ({ pending: t.pending, approved: t.approved, rejected: t.rejected }[status] || status);
const getLeaveTypeText = (type, t)  => ({ annual: t.annual, sick: t.sick, maternity: t.maternity, emergency: t.emergency, wfh: t.wfh }[type] || type);

const LeaveRequestItem = ({ leave, theme, language, isRTL, t, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const normalizeStatus    = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
  const normalizeLeaveType = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

  const normalizedStatus = normalizeStatus(leave.status);
  const normalizedType   = normalizeLeaveType(leave.leaveType || leave.type);
  const StatusIcon       = getStatusIcon(normalizedStatus);
  const TypeIcon         = getLeaveTypeIcon(normalizedType);
  const duration         = leave.days || calculateDuration(leave.startDate, leave.endDate);
  const statusColor      = getStatusColor(normalizedStatus, theme);

  const requestItemStyle = {
    background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    border: theme === "dark" ? "2px solid rgba(255,255,255,0.08)" : "2px solid rgba(0,0,0,0.05)",
    borderRadius: "16px",
    padding: "clamp(16px, 3vw, 20px)",
    marginBottom: "16px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    direction: isRTL ? "rtl" : "ltr",
    transform: isHovered ? "translateY(-8px)" : "translateY(0)",
  };

  return (
    <div
      style={requestItemStyle}
      onClick={onClick}
      className="request-item"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>

        {/* Icon */}
        <div style={{
          width: "clamp(48px,8vw,56px)", height: "clamp(48px,8vw,56px)",
          background: `${statusColor}20`, borderRadius: "12px",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <TypeIcon size={window.innerWidth < 768 ? 20 : 24} color={statusColor} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: "200px" }}>
          <h4 style={{
            color: theme === "dark" ? "white" : "#1a1a1a",
            fontSize: "clamp(16px,2.5vw,18px)", fontWeight: "600",
            margin: "0 0 8px 0", textAlign: isRTL ? "right" : "left",
          }}>
            {leave.employeeName || t.unknownEmployee}
          </h4>
          <p style={{
            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
            fontSize: "14px", margin: "0 0 4px 0", textAlign: isRTL ? "right" : "left",
          }}>
            {getLeaveTypeText(normalizedType || "unknown", t)} {t.leave} • {duration} {t.days}
          </p>
          <p style={{
            color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
            fontSize: "13px", margin: 0,
            display: "flex", alignItems: "center", gap: "6px",
            textAlign: isRTL ? "right" : "left",
          }}>
            <Calendar size={12} />
            {new Date(leave.startDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
            {" — "}
            {new Date(leave.endDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
          </p>
        </div>

        {/* Badge + Chevron */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{
            padding: "6px 12px", background: `${statusColor}20`, color: statusColor,
            borderRadius: "8px", fontSize: "12px", fontWeight: "600",
            textTransform: "capitalize", display: "inline-flex", alignItems: "center", gap: "6px",
          }}>
            <StatusIcon size={14} />
            {getStatusText(normalizedStatus, t)}
          </span>
          <ChevronRight
            size={20}
            color={theme === "dark" ? "rgba(255,255,255,0.4)" : "rgba(26,26,26,0.4)"}
            style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
          />
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestItem;